// src/hooks/useRegistration.js - UPDATED with password integration
import { useCallback, useMemo, useState } from 'react';
import { RegistrationController } from '../controllers/RegistrationController';
import { FirebaseService } from '../services/FirebaseService';

export const useRegistration = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  
  const registrationController = useMemo(() => new RegistrationController(), []);

  // Validate registration step
  const validateStep = useCallback((stepNumber, formData) => {
    const validation = registrationController.validateStep(stepNumber, formData);
    
    if (!validation.isValid) {
      setValidationErrors({ [stepNumber]: validation.errors });
    } else {
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated[stepNumber];
        return updated;
      });
    }
    
    return validation;
  }, [registrationController]);

  // Get service enhancements for Step 3
  const getServiceEnhancements = useCallback((formData) => {
    return registrationController.getServiceEnhancements(formData);
  }, [registrationController]);

  // Add custom service
  const addCustomService = useCallback((formData, customServiceData) => {
    return registrationController.addCustomService(formData, customServiceData);
  }, [registrationController]);

  // UPDATED: Complete registration with password
  const completeRegistration = useCallback(async (formData) => {
    setIsProcessing(true);
    setError(null);
    
    try {
      console.log('🔄 Starting registration process...');
      
      // Validate all registration data (including password)
      const validation = registrationController.validateRegistrationData(formData);
      
      if (!validation.isValid) {
        setValidationErrors({ final: validation.errors });
        return { success: false, errors: validation.errors };
      }

      // Pre-check duplicate phone number before any auth calls
      const duplicateCheck = await FirebaseService.precheckDuplicateBusinessPhone(formData.phoneNumber);

      if (duplicateCheck.duplicate) {
        const duplicateMessage = 'A business with this phone number already exists. Please use a different phone number or sign in to continue.';
        setValidationErrors({ final: [duplicateMessage] });
        setError(duplicateMessage);
        return {
          success: false,
          error: duplicateMessage,
          code: duplicateCheck.error?.code,
          status: duplicateCheck.error?.httpStatus
        };
      }

      // UPDATED: Prepare Firebase data with password included
      const { firebaseData, userData } = registrationController.prepareFirebaseData(validation.models, formData);
      
      console.log('🔄 Creating Firebase account...');
      
      // Create user account and business profile with real password
      const result = await FirebaseService.registerNewBusiness(userData, firebaseData);
      
      if (result.success) {
        console.log('✅ Registration completed successfully!');
        setValidationErrors({});
        return { 
          success: true, 
          userId: result.userId,
          businessId: result.businessId,
          emailVerificationSent: result.emailVerificationSent
        };
      } else {
        setValidationErrors({ final: [result.error] });
        setError(result.error);
        return {
          success: false,
          error: result.error,
          code: result.code,
          status: result.status
        };
      }
      
    } catch (error) {
      console.error('❌ Registration failed:', error);
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setIsProcessing(false);
    }
  }, [registrationController]);

  return {
    isProcessing,
    error,
    validationErrors,
    validateStep,
    getServiceEnhancements,
    addCustomService,
    completeRegistration
  };
};
